import { useCallback, useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';

/**
 * Restores a soft-deleted command through `POST /v3/api/commands/{id}/restore`.
 *
 * Deleted commands stay visible (greyed out) in the list and keep their detail
 * view, so restoring is offered from the list row, the show page and from the
 * 422 conflict the create page gets when the name belongs to a deleted command.
 */
export const useRestoreCommand = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [isRestoring, setIsRestoring] = useState(false);

  const restore = useCallback(
    async (id: number | string, name?: string): Promise<boolean> => {
      setIsRestoring(true);

      try {
        await dataProvider.post(`commands/${id}/restore`, {});
        notify('resources.commands.restore.success', {
          type: 'success',
          messageArgs: { name: name ?? id },
        });

        return true;
      } catch (error: any) {
        notify('resources.commands.restore.error', {
          type: 'error',
          messageArgs: { name: name ?? id, error: error?.message ?? '' },
        });

        return false;
      } finally {
        setIsRestoring(false);
      }
    },
    [dataProvider, notify],
  );

  return { restore, isRestoring };
};
